import React from 'react';
import { Badge } from '../../components/ui/Badge/Badge';
import { Avatar } from '../../components/ui/Avatar/Avatar';
import { Table } from '../../components/ui/Table';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer/PageContainer';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog/ConfirmDialog';
import { TableActions } from '@/components/ui/TableActions';
import { TableActionButton } from '@/components/ui/TableActionButton';
import { Button } from '@/components/ui/Button';
import { apiFetch } from '@/lib/api';
import { formatDate, formatTime } from '@/lib/localeFormatters';
import { useNotificationStore } from '@/stores/notificationStore';
import { Send, Trash2, ShieldOff, Shield, Plus } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { t } from '@/lib/i18n';
import { InviteSuperAdminDialog } from './InviteSuperAdminDialog';

export function UsersList() {
  usePageHeader({ title: t('Users'), breadcrumbs: [{ label: t('Admin'), path: '/admin/orgs' }] });

  const [loading, setLoading] = React.useState(false);
  const [users, setUsers] = React.useState<Array<any>>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [totalItems, setTotalItems] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [currentPageSize, setCurrentPageSize] = React.useState(15);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [filters, setFilters] = React.useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = React.useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);

  // Revoke invite confirmation state
  const [revokeTarget, setRevokeTarget] = React.useState<{ id: string; email: string } | null>(null);
  const [revoking, setRevoking] = React.useState(false);
  const [resendingId, setResendingId] = React.useState<string | null>(null);

  const notifications = useNotificationStore();
  const currentUser = useAuthStore((s) => s.user);
  const [togglingSuperId, setTogglingSuperId] = React.useState<string | null>(null);
  const [inviteDialogOpen, setInviteDialogOpen] = React.useState(false);

  const fetchUsers = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: String(currentPage),
        pageSize: String(currentPageSize),
        ...(searchTerm && { search: searchTerm }),
        ...(filters.inviteStatus && { inviteStatus: filters.inviteStatus }),
        ...(filters.accountStatus && { accountStatus: filters.accountStatus }),
        ...(filters.role && { role: filters.role }),
        ...(sortConfig && {
          sortBy: sortConfig.key,
          sortOrder: sortConfig.direction
        })
      });

      const res = await apiFetch(`/api/admin/users-with-invites?${params}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to load');

      setUsers(json.data || []);
      setTotalItems(json.pagination?.total || 0);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [currentPage, currentPageSize, searchTerm, filters, sortConfig]);

  React.useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  };

  const handleFilter = (newFilters: Record<string, string>) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    setSortConfig({ key, direction });
    setCurrentPage(1);
  };

  const handlePaginate = (page: number, pageSize: number) => {
    setCurrentPage(page);
    setCurrentPageSize(pageSize);
  };

  const handleResend = async (inviteId: string) => {
    setResendingId(inviteId);
    try {
      const res = await apiFetch(`/api/admin/invites/${inviteId}/resend`, { method: 'POST' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to resend');
      notifications.success(t('Invite resent successfully'));
      fetchUsers();
    } catch (e: any) {
      notifications.error(e.message || t('Failed to resend invite'));
    } finally {
      setResendingId(null);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const res = await apiFetch(`/api/admin/invites/${revokeTarget.id}`, { method: 'DELETE' });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || 'Failed to revoke');
      notifications.success(t('Invite revoked successfully'));
      setRevokeTarget(null);
      fetchUsers();
    } catch (e: any) {
      notifications.error(e.message || t('Failed to revoke invite'));
    } finally {
      setRevoking(false);
    }
  };

  const handleRevokeSuperAdmin = async (userId: string, email: string) => {
    if (!window.confirm(`${t('Remove super admin privileges from')} ${email}?`)) return;

    setTogglingSuperId(userId);
    try {
      const res = await apiFetch(`/api/admin/users/${userId}/super-admin`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_super_admin: false }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error?.message || t('Failed to update'));
      notifications.success(`${t('Super admin removed from')} ${email}`);
      fetchUsers();
    } catch (e: any) {
      notifications.error(e.message || t('Failed to update super admin status'));
    } finally {
      setTogglingSuperId(null);
    }
  };

  const columns = [
    {
      key: 'user',
      title: t('User'),
      render: (_: any, record: any) => {
        const isInvite = record.user_status === 'pending' || record.user_status === 'expired';
        return (
          <div className="flex items-center gap-3">
            {isInvite ? (
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
            ) : (
              <Avatar
                size="sm"
                src={record.avatar_url || undefined}
                initials={record.full_name ? record.full_name.charAt(0).toUpperCase() : record.email.charAt(0).toUpperCase()}
                name={record.full_name || record.email}
              />
            )}
            <div>
              <div className={`font-medium ${isInvite ? 'text-muted-foreground italic' : 'text-foreground'}`}>
                {record.full_name || (isInvite ? 'Invited user' : 'No name provided')}
              </div>
              <div className="text-sm text-muted-foreground">{record.email}</div>
            </div>
          </div>
        );
      }
    },
    {
      key: 'user_status',
      title: t('Invite Status'),
      render: (value: string) => {
        switch (value) {
          case 'registered':
            return <Badge variant="success">{t('Registered')}</Badge>;
          case 'pending':
            return <Badge variant="warning">{t('Pending')}</Badge>;
          case 'expired':
            return <Badge variant="error">{t('Expired')}</Badge>;
          default:
            return <Badge variant="outline">{value}</Badge>;
        }
      }
    },
    {
      key: 'organizations',
      title: t('Organizations'),
      render: (_: any, record: any) => {
        const isInvite = record.user_status === 'pending' || record.user_status === 'expired';
        if (isInvite) {
          if (record.grants_super_admin) {
            return (
              <Badge variant="secondary" className="text-xs italic">
                {t('Platform (super admin)')}
              </Badge>
            );
          }
          if (record.invite_org_name) {
            return (
              <div className="flex flex-wrap gap-1">
                <Badge variant="outline" className="text-xs italic">
                  {record.invite_org_name}
                  {record.invite_role && (
                    <span className="ml-1 text-muted-foreground">({record.invite_role})</span>
                  )}
                </Badge>
              </div>
            );
          }
          return <span className="text-muted-foreground text-sm">--</span>;
        }

        const orgs = record.organizations || [];
        if (orgs.length === 0) {
          return <span className="text-muted-foreground text-sm">None</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {orgs.map((org: { id: string; name: string }) => (
              <Badge key={org.id} variant="outline" className="text-xs">
                {org.name}
              </Badge>
            ))}
          </div>
        );
      }
    },
    {
      key: 'is_email_verified',
      title: t('Status'),
      sortable: true,
      render: (_: boolean, record: any) => {
        const isInvite = record.user_status === 'pending' || record.user_status === 'expired';
        if (isInvite) {
          const expiresDate = record.invite_expires_at ? new Date(record.invite_expires_at) : null;
          const isExpired = expiresDate ? expiresDate < new Date() : false;
          return (
            <div className="flex flex-col gap-1">
              {record.grants_super_admin && (
                <Badge variant="secondary" className="w-fit">{t('Super Admin')}</Badge>
              )}
              {expiresDate ? (
                <div className={isExpired ? 'text-error-600' : 'text-muted-foreground'}>
                  <div className="text-xs">{isExpired ? t('Expired') : t('Expires')}</div>
                  <div className="text-sm">{formatDate(expiresDate)}</div>
                </div>
              ) : (
                <span className="text-muted-foreground text-sm">--</span>
              )}
            </div>
          );
        }
        return (
          <div className="flex flex-wrap gap-1">
            {record.is_super_admin && <Badge variant="secondary">{t('Super Admin')}</Badge>}
            <Badge variant={record.is_email_verified ? 'success' : 'warning'}>
              {record.is_email_verified ? t('Verified') : t('Unverified')}
            </Badge>
          </div>
        );
      }
    },
    {
      key: 'last_login_at',
      title: t('Last Login'),
      sortable: true,
      render: (value: string | null, record: any) => {
        const isInvite = record.user_status === 'pending' || record.user_status === 'expired';
        if (isInvite) return <span className="text-muted-foreground text-sm">--</span>;
        if (!value) return <span className="text-muted-foreground text-sm">{t('Never')}</span>;
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'created_at',
      title: t('Registered / Invited'),
      sortable: true,
      render: (value: string) => {
        const date = new Date(value);
        return (
          <div>
            <div className="text-sm">{formatDate(date)}</div>
            <div className="text-xs text-muted-foreground">{formatTime(date)}</div>
          </div>
        );
      }
    },
    {
      key: 'actions',
      title: '',
      render: (_: any, record: any) => {
        const isInvite = record.user_status === 'pending' || record.user_status === 'expired';
        if (isInvite) {
          return (
            <TableActions>
              <TableActionButton
                icon={Send}
                onClick={() => handleResend(record.id)}
                disabled={resendingId === record.id}
              >
                {resendingId === record.id ? t('Sending...') : t('Resend')}
              </TableActionButton>
              <TableActionButton
                variant="destructive"
                icon={Trash2}
                onClick={() => setRevokeTarget({ id: record.id, email: record.email })}
              >
                Revoke
              </TableActionButton>
            </TableActions>
          );
        }

        if (!record.is_super_admin) {
          return null;
        }
        const isCurrentUser = currentUser?.id === record.id;
        return (
          <TableActions>
            <TableActionButton
              variant="destructive"
              icon={ShieldOff}
              onClick={() => handleRevokeSuperAdmin(record.id, record.email)}
              disabled={isCurrentUser || togglingSuperId === record.id}
              title={isCurrentUser ? t('You cannot change your own super admin status') : undefined}
            >
              {togglingSuperId === record.id ? t('Updating...') : t('Revoke Admin')}
            </TableActionButton>
          </TableActions>
        );
      }
    }
  ];

  const filterOptions = [
    {
      key: 'inviteStatus',
      label: t('Registration'),
      options: [
        { label: t('Registered'), value: 'registered' },
        { label: t('Pending Invite'), value: 'pending' },
        { label: t('Expired Invite'), value: 'expired' }
      ]
    },
    {
      key: 'accountStatus',
      label: t('Account Status'),
      options: [
        { label: t('Active'), value: 'active' },
        { label: t('Inactive'), value: 'inactive' },
        { label: t('Email not verified'), value: 'unverified' }
      ]
    },
    {
      key: 'role',
      label: t('Role'),
      options: [
        { label: t('Super Admin'), value: 'super_admin' },
        { label: t('Regular User'), value: 'regular' }
      ]
    }
  ];

  return (
    <PageContainer>
      <div className="flex items-center justify-end mb-4 gap-2">
        <Button onClick={() => setInviteDialogOpen(true)} variant="primary">
          <Shield className="w-4 h-4 mr-1.5" />
          {t('Invite Super Admin')}
        </Button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="text-sm text-red-600">{error}</div>
        </div>
      )}

      <Table
        data={users}
        columns={columns}
        loading={loading}
        searchPlaceholder="Search users and invitations by email or name..."
        filters={filterOptions}
        pagination={{
          pageSize: 15,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 25, 50]
        }}
        emptyText="No users or invitations found."
        serverSide={true}
        onSearch={handleSearch}
        onFilter={handleFilter}
        onSort={handleSort}
        onPaginate={handlePaginate}
        totalItems={totalItems}
      />

      <ConfirmDialog
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        onConfirm={handleRevoke}
        title={t('Revoke Invitation')}
        description={`${t('Are you sure you want to revoke the invitation for')} ${revokeTarget?.email}? ${t('This action cannot be undone.')}`}
        confirmText={t('Revoke')}
        variant="destructive"
        isLoading={revoking}
      />

      <InviteSuperAdminDialog
        isOpen={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onSuccess={fetchUsers}
      />
    </PageContainer>
  );
}
